import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { TIMER_CONSTANTS } from '../constants/timerConstants';
import logger from './../../../utils/logger.js';


const InterviewTimer = ({ questionTimer, showEditor, sendSkipRequest, question, id, submitQuestion, setModalType, setModalOpen, autoSaveCode }) => {
  const [countdown, setCountdown] = useState(null);
  const initializedTimerRef = useRef({ timer: null, id: null });
  const timerIntervalRef = useRef(null);
  // Track shown confirmation modals to avoid duplicates
  const shownTimeModals = useRef({});
  // Track the last time we auto-submitted (in seconds from the countdown)
  const lastAutoSubmitRef = useRef(null);

  // logger.debug('InterviewTimer props:', { questionTimer, showEditor, countdown });

  // Function to parse time string (mm:ss) to seconds
  const parseTimeToSeconds = (timeString) => {
    if (!timeString) return 0;
    
    const parts = timeString.split(':');
    if (parts.length === 2) {
      const minutes = parseInt(parts[0], 10);
      const seconds = parseInt(parts[1], 10);
      if (!isNaN(minutes) && !isNaN(seconds)) {
        return minutes * 60 + seconds;
      }
    } else if (parts.length === 1) {
      const minutes = parseInt(parts[0], 10);
      if (!isNaN(minutes)) {
        return minutes * 60;
      }
    }
    return 0;
  };
  
  // Function to format seconds to mm:ss
  const formatSecondsToTime = (totalSeconds) => {
    if (totalSeconds <= 0) return "00:00";
    
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Clean up timer resources
  const cleanupTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };
  
  // Reset and initialize timer with new values
  const resetAndStartTimer = (timer, questionId) => {
    // Clean up existing timer
    cleanupTimer();
    
    // Update reference
    initializedTimerRef.current = { timer, id: questionId };
    
    // Parse and set countdown
    const initialSeconds = parseTimeToSeconds(timer);
    setCountdown(initialSeconds);
    
    logger.debug(`Timer reset and started with ${initialSeconds} seconds for question ID: ${questionId}`);
  };
  
  // Effect to handle timer initialization when questionTimer or id changes
  useEffect(() => {
    // Only proceed if both timer and id are available and we're not showing editor
    if (questionTimer && id ) {
      // Check if timer or id has changed
      const timerChanged = initializedTimerRef.current.timer !== questionTimer;
      const idChanged = initializedTimerRef.current.id !== id;
      
      if (timerChanged || idChanged) {
        logger.debug(`Timer needs reset because: timer changed: ${timerChanged}, id changed: ${idChanged}`);
        resetAndStartTimer(questionTimer, id);
      }
    
    } else if (!questionTimer || !id) {
      // Clear timer if no valid question timer or id
      logger.debug('No valid timer or id, clearing timer');
      setCountdown(null);
      initializedTimerRef.current = { timer: null, id: null };
      cleanupTimer();
    }
  }, [questionTimer, id, showEditor]);

  // Separate effect for handling timer thresholds - will not re-run on every second
  useEffect(() => {
    const checkTimeThresholds = (seconds) => {
      // Show confirmation modal at specific times if setModalType and setModalOpen are available
      const timeThresholds = TIMER_CONSTANTS.CONFIRMATION_THRESHOLDS;
      if (timeThresholds.includes(seconds) && setModalType && setModalOpen) {
        // Only show if we haven't shown it at this time threshold before
        if (!shownTimeModals.current[seconds]) {
          logger.debug(`Showing confirmation modal at ${seconds} seconds remaining`);
          setModalType('submit_code');
          setModalOpen(true);
          shownTimeModals.current[seconds] = true; // Mark as shown
        }
      }
    };

    // Store in ref to avoid dependency issues
    window._checkTimeThresholds = checkTimeThresholds;
  }, [setModalType, setModalOpen]);

  // Main timer effect with minimal dependencies
  useEffect(() => {
    if (countdown > 0 && id && !timerIntervalRef.current) {
      logger.debug(`Starting countdown from ${countdown} seconds`);
      
      // Reset shown modals when starting a new timer
      shownTimeModals.current = {};
      
      // Get the time at which we start the timer
      const startTime = Date.now();
      let expectedTime = startTime;
      
      timerIntervalRef.current = setInterval(() => {
        expectedTime += 1000; // Every execution should be 1000ms apart
        
        setCountdown(prevTime => {
          if (prevTime <= 0) return 0;
          const newTime = prevTime - 1;
  
          if (newTime === 0) {
            cleanupTimer();
  
            if (id) {
              toast("Time's up! Submitting your answer...");
  
              if (submitQuestion && typeof submitQuestion === 'function') {
                logger.debug('Calling submitQuestion due to timer end');
                submitQuestion();
              } else if (sendSkipRequest && typeof sendSkipRequest === 'function') {
                logger.debug('Calling sendSkipRequest due to timer end');
                sendSkipRequest();
              }
            }
          }
          
          // Check if we need to auto-submit every 3 minutes (180 seconds)
          // We check if the current time is divisible by 180 and different from the last auto-submit time
          logger.debug(`Checking auto-submit at ${newTime} seconds`, typeof autoSaveCode === 'function');
          if (newTime > 0 && newTime % 180 === 0 && newTime !== lastAutoSubmitRef.current) {
            lastAutoSubmitRef.current = newTime;
            
            if (autoSaveCode && typeof autoSaveCode === 'function') {
              logger.debug(`Auto-submitting at ${newTime} seconds (3-minute interval)`);
              // toast.success("Auto-saving your progress...");
              autoSaveCode();
            }
          }
          
          // Check time thresholds from the stored function
          if (window._checkTimeThresholds) {
            window._checkTimeThresholds(newTime);
          }
  
          return newTime;
        });
        
        // Calculate drift and adjust timer if needed
        const drift = Date.now() - expectedTime;
        if (drift > 100) { // Only adjust if drift is significant
          logger.debug(`Timer drift detected: ${drift}ms, adjusting...`);
          expectedTime = Date.now();
        }
      }, 1000);
    }
  
    return cleanupTimer;
  }, [countdown, id, submitQuestion]); // Minimal dependencies for better performance
  

  return (
    <div style={{
      position: 'absolute', 
      left: '50%', 
      top: '35%', 
      transform: 'translate(-50%, -50%)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '6px 16px', 
      borderRadius: '20px', 
      backgroundColor: countdown <= 30 ? '#ffeeee' : '#f8f9fa',
      boxShadow: countdown <= 30 ? '0 2px 8px rgba(255,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.1)', 
      fontWeight: 'bold', 
      fontSize: '14px', 
      letterSpacing: '0.5px', 
      color: countdown <= 60 ? '#FF3333' : '#343a40',
      zIndex: 1002
    }}>
      <span role="img" aria-label="Timer" style={{ marginRight: '8px', fontSize: '16px' }}>⏱️</span>
      {formatSecondsToTime(countdown)}
    </div>
  );
};

export default InterviewTimer;
